import React from 'react'
import { AddCircle, CloseCircle, Eye } from 'iconsax-react'
import { useTheme } from '@mui/material'

import CommonButton from '../CommonButton'
import Tooltip from '@SonarCX/components/atoms/Tooltip'
import MediaAvatar from '@SonarCX/components/atoms/MediaAvatar'
import UserSearchDropdown from '@SonarCX/components/molecules/UserSearchDropdown'

import { sortByName, uniqueArray } from '@SonarCX/utilities'
import { useUserAPI } from '@SonarCX/providers/UserAPIProvider'
import useTaskMultiEdit from '../hooks/useTaskMultiEdit'
import useStyles from '../commonStyles'

const SetWatchers: React.FC = () => {
  const theme = useTheme()

  const { allUsers } = useUserAPI()
  const { selected, patchTasks } = useTaskMultiEdit()

  const [value, setValue] = React.useState([])
  const sortedAssignees = React.useMemo(() => {
    const users = uniqueArray(value).map(id => {
      const user = allUsers[id]

      if (!user) {
        return null
      }

      return {
        ...user
      }
    })

    return users
      .filter(Boolean)
      .sort(sortByName)
      .map(i => i.id)
  }, [allUsers, value])

  const classes = useStyles()

  const handleClose = React.useCallback(() => {
    setValue([])
  }, [])

  const handleAccept = React.useCallback(async () => {
    const updates: TaskPatch[] = selected.map(id => ({
      id,
      watcher_ids: uniqueArray(value)
    }))
    await patchTasks({ updates, message: 'Watchers updated' })
    handleClose()
  }, [selected, value, patchTasks, handleClose])

  const removeUser = React.useCallback(
    (id: string) => {
      setValue(value.filter(i => i !== id))
    },
    [value]
  )

  return (
    <CommonButton
      title='Set watchers'
      Icon={Eye}
      onAccept={handleAccept}
      onClose={handleClose}
    >
      <div className={classes.content}>
        <div className={classes.title}>Watchers</div>

        <div className={classes.center}>
          {sortedAssignees.map(id => {
            const user = allUsers[id]

            if (!user) {
              return null
            }

            return (
              <Tooltip key={id} title={user?.name ?? ''} arrow>
                <div className={classes.dismiss}>
                  <MediaAvatar
                    key={id}
                    user={user}
                    className={classes.avatar}
                  />

                  <CloseCircle
                    size={theme.size.sm}
                    variant='Bulk'
                    color='grey'
                    onClick={() => removeUser(user.id)}
                    className='close'
                  />
                </div>
              </Tooltip>
            )
          })}

          <UserSearchDropdown
            prevSelected={[]}
            selected={null}
            onSelect={id => setValue(uniqueArray([...value, id]))}
            limit={10}
            disablePortal
          >
            <div className={classes.center}>
              <AddCircle size={theme.size.base} variant='Bulk' color='grey' />
              Add
            </div>
          </UserSearchDropdown>
        </div>
      </div>
    </CommonButton>
  )
}

export default SetWatchers
